// 진행과 완료 기록은 분리한다. 비교용 완료 기록을 현재 진행으로 덮어쓰지 않는다.
// 사건·직업 기준 데이터 사본(referenceData)은 생마다 넣지 않고 지문(fingerprint)별로 한 번만 별도 키에 둔다.
// 브라우저 저장 공간이 가득 차면 가장 오래된 보관 기록부터 지우고(비교 대상은 제외) 안내를 남긴다.
import { getEventHistory } from './events.js';
const KEY = 'saju-run-v1';
const LIVES_KEY = 'saju-lives-v1';
const REFS_KEY = 'saju-reference-v1';

const stable = (value) => {
  if (value == null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stable(value[key])}`).join(',')}}`;
};
const fingerprint = (value) => {
  let result = 2166136261;
  for (const char of stable(value)) result = Math.imul(result ^ char.charCodeAt(0), 16777619) >>> 0;
  return result.toString(36);
};

export const newRunId = () => `life-${Date.now().toString(36)}-${newSeed().toString(36)}`;
export const runIdentity = (run) => run.runId ?? `legacy-${fingerprint({ seed: run.seed, birth: run.birth, saju: run.saju })}`;

// 비교 시작 조건은 출생 시점의 스냅샷이다. API 재계산이나 현재 서버 기준으로 교체하지 않는다.
export function replayBaseline(run) {
  if (run.replayBaseline) return structuredClone(run.replayBaseline);
  const baseline = { seed: run.seed, birth: run.birth, saju: run.saju, deathAge: run.deathAge, birthYear: run.birthYear };
  for (const key of ['balanceVersion', 'rulesVersion', 'engineRulesVersion', 'engineVersion', 'eventCatalogVersion', 'initialStatsVersion', 'revisions', 'referenceData']) {
    if (run[key] != null) baseline[key] = run[key];
  }
  return structuredClone(baseline);
}

export function sameStart(left, right) {
  if (!left || !right) return false;
  const pick = (run) => {
    const base = run.replayBaseline ?? run;
    return { seed: base.seed, birth: base.birth, saju: base.saju, deathAge: base.deathAge };
  };
  return stable(pick(left)) === stable(pick(right));
}

// ───── 사본 공유: referenceData 를 지문별로 한 번만 저장한다 ─────
const isFull = (data) => !!data && typeof data === 'object' && !data.shared && typeof data.fingerprint === 'string' && Array.isArray(data.events);
const isShared = (data) => !!data && typeof data === 'object' && data.shared === true && typeof data.fingerprint === 'string';

/** 사본을 refs 로 옮기고 그 자리에는 지문만 남긴 복사본을 돌려준다. 원본은 바꾸지 않는다. 지문이 없는 아주 옛 사본은 그대로 둔다. */
function externalize(run, ctx) {
  if (!run || typeof run !== 'object') return { run, changed: false };
  let changed = false;
  const take = (data) => {
    if (!isFull(data)) return data;
    if (!ctx.refs[data.fingerprint]) { ctx.refs[data.fingerprint] = data; ctx.dirty = true; }
    changed = true;
    return { fingerprint: data.fingerprint, version: data.version ?? null, shared: true };
  };
  const out = { ...run };
  if ('referenceData' in run) out.referenceData = take(run.referenceData);
  if (run.replayBaseline && typeof run.replayBaseline === 'object' && 'referenceData' in run.replayBaseline) {
    out.replayBaseline = { ...run.replayBaseline, referenceData: take(run.replayBaseline.referenceData) };
  }
  return { run: changed ? out : run, changed };
}

/** 지문만 남은 사본을 되살린다. 저장소에 사본이 없으면 null (이후 화면이 필요할 때 다시 받는다). */
function hydrate(run, refs) {
  if (!run || typeof run !== 'object') return run;
  const fill = (data) => (isShared(data) ? (refs[data.fingerprint] ? structuredClone(refs[data.fingerprint]) : null) : data);
  let out = run;
  if (isShared(run.referenceData)) out = { ...out, referenceData: fill(run.referenceData) };
  if (run.replayBaseline && isShared(run.replayBaseline.referenceData)) {
    out = { ...out, replayBaseline: { ...run.replayBaseline, referenceData: fill(run.replayBaseline.referenceData) } };
  }
  return out;
}

const getRaw = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
function readRefs() {
  try {
    const parsed = JSON.parse(getRaw(REFS_KEY));
    return parsed?.items && typeof parsed.items === 'object' && !Array.isArray(parsed.items) ? parsed.items : {};
  } catch { return {}; }
}
// 보관 기록 읽기: 없으면 빈 목록, 형식이 깨졌으면 ok=false (깨진 데이터는 덮어쓰지 않는다)
function readLivesRaw() {
  const raw = getRaw(LIVES_KEY);
  if (raw == null) return { ok: true, lives: [] };
  try {
    const parsed = JSON.parse(raw);
    const lives = Array.isArray(parsed) ? parsed : parsed?.lives;
    return Array.isArray(lives) ? { ok: true, lives } : { ok: false, lives: [] };
  } catch { return { ok: false, lives: [] }; }
}

export function loadLives() {
  try {
    const { ok, lives } = readLivesRaw();
    if (!ok) return [];
    const refs = readRefs();
    return lives.filter((run) => run?.status === 'ended').map((run) => migrateRun(hydrate(run, refs)));
  } catch { return []; }
}

// ───── 저장 공간 부족 처리 ─────
let lastNotice = null;
/** 저장 중 오래된 보관 기록을 지웠다면 { droppedLives } 를 한 번만 돌려준다. */
export const takeStorageNotice = () => { const notice = lastNotice; lastNotice = null; return notice; };
const isQuotaError = (error) => !!error && (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED' || error.code === 22 || error.code === 1014);

function restore(snapshot) {
  // 저장하기 전 상태로 되돌린다. 모두 예전에 들어갔던 값이라 다시 들어간다.
  for (const [key, raw] of [[REFS_KEY, snapshot.refs], [LIVES_KEY, snapshot.lives], [KEY, snapshot.key]]) {
    try { if (raw == null) localStorage.removeItem(key); else localStorage.setItem(key, raw); } catch { /* 되돌리지 못하면 어쩔 수 없다 */ }
  }
}

function referencedFingerprints(...runs) {
  const set = new Set();
  for (const run of runs) {
    for (const data of [run?.referenceData, run?.replayBaseline?.referenceData]) if (isShared(data) || isFull(data)) set.add(data.fingerprint);
  }
  return set;
}
// 어디에서도 쓰이지 않는 사본을 지운다. 줄어드는 쓰기라 실패해도 상관없다.
function collectUnusedRefs() {
  try {
    const refs = readRefs();
    if (!Object.keys(refs).length) return;
    let key = null;
    try { key = JSON.parse(getRaw(KEY)); } catch { key = null; }
    const { lives } = readLivesRaw();
    const used = referencedFingerprints(key, ...lives);
    const kept = Object.fromEntries(Object.entries(refs).filter(([fp]) => used.has(fp)));
    if (Object.keys(kept).length !== Object.keys(refs).length) localStorage.setItem(REFS_KEY, JSON.stringify({ version: 1, items: kept }));
  } catch { /* ignore */ }
}

/**
 * 진행 기록(run)과 완료 기록(archive)을 저장한다. 성공하면 true.
 * - 사본은 한 번만 두고, 저장 공간이 가득 차면 가장 오래된 보관 기록부터 지워 자리를 만든다.
 *   현재 저장 중인 생과 비교 대상(comparisonRunId, replayOfRunId)은 지우지 않는다.
 * - 끝내 저장하지 못하면 저장하기 전 상태로 되돌리고 false.
 */
function commit({ run = null, archive = null } = {}) {
  const snapshot = { key: getRaw(KEY), refs: getRaw(REFS_KEY), lives: getRaw(LIVES_KEY) };
  const ctx = { refs: readRefs(), dirty: false };
  const keyRun = run ? externalize(run, ctx).run : null;
  const writeRefs = () => { if (ctx.dirty) { localStorage.setItem(REFS_KEY, JSON.stringify({ version: 1, items: ctx.refs })); ctx.dirty = false; } };

  // 1) 진행 기록만 저장할 때의 빠른 길: 보관 기록은 읽지 않는다
  if (!archive) {
    const wasDirty = ctx.dirty;
    try {
      writeRefs();
      if (keyRun) localStorage.setItem(KEY, JSON.stringify(keyRun));
      if (wasDirty) collectUnusedRefs();
      return true;
    } catch (error) {
      if (!isQuotaError(error)) return false;
    }
  }

  // 2) 보관 기록을 함께 다루는 길
  const stored = readLivesRaw();
  if (!stored.ok) {
    // 깨진 보관 데이터는 덮어쓰지 않는다. 진행 기록만 저장해 본다.
    try { writeRefs(); if (keyRun) localStorage.setItem(KEY, JSON.stringify(keyRun)); } catch { /* ignore */ }
    return false;
  }
  let livesDirty = stored.lives.some((life) => life?.status !== 'ended');
  const lives = stored.lives.filter((life) => life?.status === 'ended').map((life) => {
    const migrated = externalize(migrateRun(life), ctx);
    if (migrated.changed) livesDirty = true;   // 옛 형식(사본이 안에 들어 있음)은 이번에 옮긴다
    return migrated.run;
  });
  if (archive) {
    const id = runIdentity(archive);
    const entry = structuredClone(externalize({ ...archive, runId: id }, ctx).run);
    const index = lives.findIndex((life) => runIdentity(life) === id);
    if (index >= 0) lives[index] = entry; else lives.push(entry);
    livesDirty = true;
  }
  const protectedIds = new Set([archive && runIdentity(archive), run?.comparisonRunId, run?.replayOfRunId, archive?.comparisonRunId, archive?.replayOfRunId].filter(Boolean));

  let dropped = 0, freed = false;
  for (;;) {
    try {
      writeRefs();
      if (livesDirty) localStorage.setItem(LIVES_KEY, JSON.stringify({ version: 1, lives }));
      if (keyRun) localStorage.setItem(KEY, JSON.stringify(keyRun));
      break;
    } catch (error) {
      if (!isQuotaError(error)) { restore(snapshot); return false; }
      if (livesDirty && !freed && snapshot.lives != null) {
        // 새로 쓸 보관 기록은 옛 것보다 작다. 옛 것을 먼저 비워야 사본을 넣을 자리가 생긴다.
        try { localStorage.removeItem(LIVES_KEY); } catch { /* ignore */ }
        freed = true;
        continue;
      }
      const victim = lives.findIndex((life) => !protectedIds.has(runIdentity(life)));
      if (victim < 0) { restore(snapshot); return false; }
      lives.splice(victim, 1);
      dropped += 1;
      livesDirty = true;
    }
  }
  if (dropped) lastNotice = { droppedLives: (lastNotice?.droppedLives ?? 0) + dropped };
  collectUnusedRefs();
  return true;
}

// 같은 runId는 교체한다. 결과 화면을 다시 열거나 완료 저장을 반복해도 한 생은 한 번만 남는다.
export function archiveRun(run) {
  if (run?.status !== 'ended') return true;
  try { return commit({ archive: run }); } catch { return false; }
}

// 기존 기록·능력치는 재계산하지 않는다. 새 엔진에 필요한 부가 상태만 보충한다.
export function migrateRun(run) {
  if (!run || typeof run !== 'object') return run;
  const seen = Array.isArray(run.seen) ? run.seen : [];
  const lastSeen = { ...(run.eventLastSeen ?? {}) };
  // 옛 저장에는 사건별 발생 나이가 없다. 마지막 저장 시점을 기준으로 재등장을 늦춘다.
  for (const id of seen) if (lastSeen[id] == null) lastSeen[id] = Math.max(0, (run.age ?? 1) - 1);
  return { ...run, runId: runIdentity(run), seen, eventLastSeen: lastSeen,
    log: Array.isArray(run.log) ? run.log : [], history: Array.isArray(run.history) ? run.history : [],
    people: Array.isArray(run.people) ? run.people : [], meetAge: run.meetAge ?? -1,
    eventHistory: getEventHistory(run),
    jobHistory: Array.isArray(run.jobHistory) ? run.jobHistory : [], jobChangeUsedFor: run.jobChangeUsedFor ?? null,
    balanceVersion: run.balanceVersion ?? 1 };
}

export function loadRun() {
  try {
    const current = migrateRun(hydrate(JSON.parse(getRaw(KEY)), readRefs()));
    if (!current) return null;
    const completed = loadLives().find((life) => runIdentity(life) === runIdentity(current));
    // 현재 진행 포인터 저장만 실패했어도 이미 보관한 완료 기록으로 복구할 수 있다.
    return completed && completed.age >= current.age ? completed : current;
  } catch { return null; }
}
export function saveRun(run) {
  try { return commit({ run, archive: run?.status === 'ended' ? run : null }); } catch { return false; }
}
export function clearRun() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}
// 재현용 seed (같은 seed → 같은 NPC·확률 결과)
export const newSeed = () => Math.floor(Math.random() * 2 ** 31);
